import { api } from '../api';

/**
 * Fotografia e manutenção do equipamento — as chamadas da `0053`.
 *
 * Os caminhos vão escritos por extenso, como nos outros módulos: o
 * caminho é o contrato entre a tela e a API, e contrato se lê. Quem
 * procurar `/manutencoes` no projeto acha estas linhas, e o teste `26`
 * — que confere se toda rota de criação tem caminho literal na interface
 * — consegue enxergá-las.
 */

export interface FotoEquipamento {
  id: string;
  arquivo_id: string;
  tipo: string;
  legenda: string | null;
  ordem: number;
  principal: boolean;
  manutencao_id: string | null;
  desconsiderada: boolean;
  motivo_desconsideracao: string | null;
  desconsiderada_por_nome: string | null;
  nome_original: string;
  mime: string;
  enviada_em: string;
  enviada_por_nome: string | null;
}

export interface Manutencao {
  id: string;
  equipamento_id: string;
  tipo: string;
  abertura: string;
  motivo: string;
  executante: string | null;
  executante_externo: boolean;
  numero_documento: string | null;
  encerramento: string | null;
  servico_executado: string | null;
  pecas_substituidas: string | null;
  resultado: string | null;
  exige_recalibracao: boolean | null;
  cancelada: boolean;
  motivo_cancelamento: string | null;
  observacoes: string | null;
  aberta_por_nome: string | null;
  encerrada_por_nome: string | null;
  cancelada_por_nome: string | null;
  aberta: boolean;
  /** O laudo do fornecedor, em PDF (`0113`). */
  arquivo_id: string | null;
  tem_documento: boolean;
  parada: { days?: number; hours?: number; minutes?: number } | null;
  fotos: number;
}

/** O QR da plaqueta: código patrimonial, não uuid. */
export const equipamentoPorCodigo = (codigo: string) =>
  api<Record<string, unknown>>(`/equipamentos/por-codigo/${encodeURIComponent(codigo)}`);

export const listarFotos = (id: string, todas = false) =>
  api<FotoEquipamento[]>(`/equipamentos/${id}/fotos${todas ? '?todas=1' : ''}`);

export const definirCapa = (fotoId: string) =>
  api<FotoEquipamento>(`/equipamentos/fotos/${fotoId}/capa`, { metodo: 'POST' });

export const desconsiderarFoto = (fotoId: string, motivo: string) =>
  api<FotoEquipamento>(`/equipamentos/fotos/${fotoId}/desconsiderar`,
    { metodo: 'POST', corpo: { motivo } });

export const listarManutencoes = (id: string) =>
  api<Manutencao[]>(`/equipamentos/${id}/manutencoes`);

export const abrirManutencao = (id: string, corpo: unknown) =>
  api<Manutencao>(`/equipamentos/${id}/manutencoes`, { metodo: 'POST', corpo });

export const encerrarManutencao = (manutencaoId: string, corpo: unknown) =>
  api<Manutencao>(`/equipamentos/manutencoes/${manutencaoId}/encerrar`,
    { metodo: 'POST', corpo });

export const cancelarManutencao = (manutencaoId: string, motivo: string) =>
  api<Manutencao>(`/equipamentos/manutencoes/${manutencaoId}/cancelar`,
    { metodo: 'POST', corpo: { motivo } });

export interface AutorizacaoUso {
  id: string;
  equipamento_id: string;
  motivo: string;
  impedimento_no_ato: string | null;
  autorizado_por_nome: string | null;
  autorizado_em: string;
  valida_de: string;
  valida_ate: string;
  revogada: boolean;
  motivo_revogacao: string | null;
  revogada_por_nome: string | null;
  vigente: boolean;
  usos: number;
}

export const listarAutorizacoes = (id: string) =>
  api<AutorizacaoUso[]>(`/equipamentos/${id}/autorizacoes`);

export const autorizarUso = (id: string, corpo: unknown) =>
  api<AutorizacaoUso>(`/equipamentos/${id}/autorizacoes`, { metodo: 'POST', corpo });

export const revogarAutorizacao = (autorizacaoId: string, motivo: string) =>
  api<AutorizacaoUso>(`/equipamentos/autorizacoes/${autorizacaoId}/revogar`,
    { metodo: 'POST', corpo: { motivo } });

/** Os tipos, com o texto que a tela mostra. Casa com o enum da `0053`. */
export const TIPOS_MANUTENCAO = [
  ['preventiva', 'Preventiva', 'prevista em plano; o equipamento está bem'],
  ['corretiva', 'Corretiva', 'alguma coisa parou de funcionar'],
  ['ajuste', 'Ajuste', 'mexeu na indicação: nivelamento, zero, span'],
  ['limpeza', 'Limpeza técnica', 'limpeza que exige parada'],
  ['atualizacao', 'Atualização', 'firmware ou software do equipamento'],
  ['instalacao', 'Instalação', 'montagem, mudança de bancada, transporte'],
] as const;

export const RESULTADOS_MANUTENCAO = [
  ['concluida', 'Concluída'],
  ['sem_reparo', 'Sem reparo'],
  ['substituido', 'Equipamento substituído'],
  ['descartado', 'Sem conserto — para descarte'],
] as const;

/** O roteiro de fotografia do equipamento. */
export const TIPOS_FOTO = [
  ['geral', 'Vista geral'],
  ['placa_identificacao', 'Plaqueta patrimonial'],
  ['numero_serie', 'Número de série'],
  ['mostrador', 'Mostrador'],
  ['acessorio', 'Acessório'],
  ['instalacao', 'Instalação'],
  ['avaria', 'Avaria'],
  ['manutencao', 'Manutenção'],
  ['outro', 'Outro'],
] as const;
